import { Localization } from "libs/localization/localization"
import { EColors, EFonts, EVariables } from "libs/style/w24_styling"
import { useIsWide } from "libs/utils/logic/responsiveLogic"
import React from "react"
import styled from "styled-components/macro"

interface Props {
  country: "Norway" | "Czechia" | "Denmark" | "Slovakia" | "Faroe Islands"
  className?: string
}

export const CountryDemo = (props: Props): JSX.Element => {
  const isWide = useIsWide()

  function getCountry(): {
    svg: JSX.Element
    size: number
    title: string
  } {
    switch (props.country) {
      case "Norway":
        return {
          svg: SVG_Norway,
          size: isWide ? 90 : 60,
          title: Localization.NORWAY,
        }
      case "Czechia":
        return {
          svg: SVG_Czechia,
          size: isWide ? 65 : 50,
          title: Localization.CZECHIA,
        }
      case "Denmark":
        return {
          svg: SVG_Denmark,
          size: isWide ? 60 : 45,
          title: Localization.DENMARK,
        }
      case "Slovakia":
        return {
          svg: SVG_Slovakia,
          size: isWide ? 80 : 50,
          title: Localization.SLOVAKIA,
        }
      case "Faroe Islands":
        return {
          svg: SVG_Faroe,
          size: isWide ? 50 : 40,
          title: Localization.FAROE_ISLANDS,
        }
      default:
        return {
          svg: SVG_Norway,
          size: isWide ? 60 : 60,
          title: Localization.NORWAY,
        }
    }
  }

  return (
    <S_CountryDemo className={props.className}>
      <S_Grey $size={getCountry().size}>{getCountry().svg}</S_Grey>
      <S_Title>{getCountry().title}</S_Title>
    </S_CountryDemo>
  )
}

const S_CountryDemo = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  gap: 10px;
`

const S_Grey = styled.div<{ $size: number }>`
  width: 100px;
  height: 100px;
  background-color: ${EColors.GREY2};
  border-radius: ${EVariables.Radius1};
  display: flex;
  justify-content: center;
  align-items: center;
  > svg {
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
  }
  @media only screen and (min-width: 1260px) {
    width: 150px;
    height: 150px;
    border-radius: ${EVariables.Radius2};
  }
`

const S_Title = styled.div`
  color: ${EColors.DARK};
  text-align: center;
  font-family: ${EFonts.Medium};
  font-size: 15px;
  @media only screen and (min-width: 1260px) {
    font-size: 18px;
    margin: 10px 0 0 0;
  }
`

export const SVG_Norway = (
  <svg
    width="83"
    height="94"
    viewBox="0 0 83 94"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M70.9722 36.3014L65.6136 37.1511L59.9444 36.9194L55.9837 40.3182L50.7805 40.0865L44.1793 41.8631L36.3355 47.1159L31.3653 50.2057L24.5312 58.471L19.0173 64.4962L12.7267 68.9765L4.02874 72.6843L1 75.4652L2.47552 85.8161L3.95109 90.6826L8.92139 93L13.581 91.9186L20.1822 86.6658L22.7449 89.4467L24.0652 86.8976L26.7057 83.8077L27.4046 78.4777L24.9971 76.2376L24.2205 70.3669L26.0067 66.2729L29.3461 66.3501L30.3557 64.6507L28.9578 63.183L33.3845 57.0806L36.025 52.3686L37.7335 49.356L40.8399 49.4333L41.3058 47.0386L47.441 47.7338V45.0302L49.3825 44.7985L51.0134 43.717L54.9741 45.9572L59.0901 45.7254L62.7401 46.7296L65.3806 44.8757L66.2349 41.8631L70.7392 40.4727L75.1658 42.0949L74.5446 45.0302L77.0297 44.644L82 42.9446L77.8063 40.3955L81.5341 39.314L70.9722 36.3014ZM53.4209 10.5785L49.0719 9.80605L47.5963 8.49286L42.0048 9.18808L44.0239 10.3468L42.3154 11.2737L47.5187 12.1234L53.4209 10.5785ZM33.6951 4.16709L29.9674 2.93115L26.0067 3.08564L25.2301 4.24433H21.3471L19.6385 3.08564L12.4161 4.32157L14.9012 7.02519L20.8034 9.96054L25.2301 11.042L22.9003 12.3552L29.4238 14.5953L32.8408 14.4408L33.5398 11.4282L35.8696 10.733L36.8016 8.10663L43.4027 6.71621L33.6951 4.16709ZM52.489 1.77246L45.4219 1L42.9367 1.92695L38.8207 1.15449L30.744 2.08145L34.0834 3.62637H38.0441L38.7431 4.63056L46.9751 5.17129L54.8188 4.78506L58.1582 2.93115L52.489 1.77246Z"
      fill="#FAFCFE"
      stroke="#041321"
      strokeLinejoin="round"
    />
  </svg>
)

export const SVG_Czechia = (
  <svg
    width="76"
    height="38"
    viewBox="0 0 76 38"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M30.6452 1.22786L25.893 2.59496L22.7247 1L20.2355 3.7342L12.5412 6.46837L8.69398 9.88609L1 12.8481L3.263 17.1772L4.84699 23.1013L10.7307 26.519L17.2935 32.443L25.893 37L31.7767 31.3038L35.6237 30.1646L44.6757 34.4937L49.8807 33.8101L55.0855 36.5443L56.4435 33.3544L61.422 33.5823L65.0427 32.2152L65.269 30.8481L67.3057 30.1646L67.7585 26.9746L70.2477 26.2911L71.6055 23.7848H75L69.1162 16.7215L60.9695 16.038L59.3852 11.481L51.691 10.1139L50.3332 13.5316L44.2233 10.7975L44.4495 6.92406L36.0765 5.55696L30.6452 1.22786Z"
      fill="#FAFCFE"
      stroke="#041321"
      strokeLinejoin="round"
    />
  </svg>
)

export const SVG_Denmark = (
  <svg
    width="80"
    height="65"
    viewBox="0 0 80 65"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M79 46.36L72.8891 35.56L48.8064 42.76L52.0415 51.76L70.3732 64L79 46.36ZM48.0877 28L38.7417 24.76L36.2257 19L40.8987 11.8L40.5389 1L27.5989 6.75997L22.2072 12.88L7.82936 14.32L3.51617 20.44L1 26.2L2.43766 48.16L9.98617 60.4L22.9262 63.28L33.7096 60.04L28.3179 49.24L39.4609 33.76L44.4932 36.28L48.0877 28Z"
      fill="#FAFCFE"
      stroke="#041321"
      strokeLinejoin="round"
    />
  </svg>
)

export const SVG_Faroe = (
  <svg
    width="45"
    height="53"
    viewBox="0 0 45 53"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M28.7113 51.0555V48.2223L27.756 45.3889V43.5H26.8001L22.0223 42.5555L21.067 40.6667H20.1111V42.5555L21.067 46.3333L25.8448 50.1111L28.7113 52H29.6667L28.7113 51.0555ZM33.4891 31.2222V30.2777L31.5779 28.3889L26.8001 26.5L24.8889 25.5555L22.9782 26.5V28.3889L23.9336 29.3334L27.756 30.2777L31.5779 33.1111H32.5338L33.4891 31.2222ZM10.5556 13.2777L8.64491 12.3333L3.86713 13.2777H1L1.95591 16.111L7.68901 17.9999H10.5556H13.4227L15.3333 17.0555L14.378 15.1667L10.5556 13.2777ZM34.4444 8.55545L26.8001 6.66659L21.067 3.83322L11.5115 4.77772L18.2005 15.1667L25.8448 21.7778L29.6667 23.6666V22.7221V20.8333L25.8448 16.111L24.8889 15.1667V14.2222L25.8448 13.2777H27.756L30.6226 15.1667H32.5338L34.4444 8.55545ZM44 6.66659L41.1334 4.77772L37.3116 1V5.72223V8.55545V9.49995H38.2669L41.1334 10.4445L44 6.66659Z"
      fill="#FAFCFE"
      stroke="#041321"
      strokeLinejoin="round"
    />
  </svg>
)

export const SVG_Slovakia = (
  <svg
    width="83"
    height="35"
    viewBox="0 0 83 35"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M82 9.76105L67.5672 3.04428L57.5526 5.38052L50.4834 3.6283L42.2364 7.4248L34.2834 1L28.6873 3.6283L27.8036 2.46017H23.3854L21.6182 5.67258L18.3783 6.54865L17.7889 10.6372L15.138 11.5133L14.8435 13.2655L10.1308 15.0177L3.65098 14.7257L1.88342 18.8141L1 21.1504L3.06167 27.2831L10.7198 31.9557L16.3162 34L28.3925 31.6637L29.2763 28.1593L34.8727 27.5752L41.6471 24.6548L43.4146 25.823L49.8945 23.7788L52.8399 19.3983L57.5526 18.23L73.7526 23.7788L76.698 22.0266L78.7601 14.7257L82 9.76105Z"
      fill="#FAFCFE"
      stroke="#041321"
      strokeLinejoin="round"
    />
  </svg>
)
